import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  XCircle,
  RefreshCw,
  Search,
  Download,
  FileText,
  TrendingDown,
  DollarSign,
  RotateCcw,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

// Denial reason codes
const denialReasons = [
  { code: "CO-4", description: "The procedure code is inconsistent with the modifier used" },
  { code: "CO-16", description: "Claim/service lacks information or has submission errors" },
  { code: "CO-18", description: "Duplicate claim/service" },
  { code: "CO-22", description: "Coordination of benefits issue" },
  { code: "CO-29", description: "Time limit for filing has expired" },
  { code: "CO-45", description: "Charges exceed your contracted/legislated fee arrangement" },
  { code: "CO-50", description: "These are non-covered services" },
  { code: "CO-97", description: "Payment adjusted because the benefit has already been paid" },
  { code: "PR-1", description: "Deductible amount" },
  { code: "PR-2", description: "Coinsurance amount" },
  { code: "PR-3", description: "Co-payment amount" },
];

// CPT code pricing
const cptPricing: Record<string, number> = {
  "99201": 45, "99202": 75, "99203": 110, "99204": 165, "99205": 210,
  "99211": 25, "99212": 45, "99213": 75, "99214": 110, "99215": 150,
};

export default function DenialsManagement() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReason, setSelectedReason] = useState<string>("all");
  const [selectedClaims, setSelectedClaims] = useState<string[]>([]);
  const [appealDialogOpen, setAppealDialogOpen] = useState(false);
  const [selectedClaimForAppeal, setSelectedClaimForAppeal] = useState<any>(null);
  const [appealNotes, setAppealNotes] = useState("");

  const { data: deniedSessions = [], isLoading } = useQuery({
    queryKey: ["denied-claims"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .eq("billing_status", "denied")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  const { data: intakeForms = [] } = useQuery({
    queryKey: ["denials-intake-forms"],
    queryFn: async () => {
      const { data, error } = await supabase.from("patients").select("*");
      if (error) throw error;
      return data || [];
    },
  });

  // Add denial reason simulation (in production, this would come from the database)
  const deniedClaims = deniedSessions.map((session, index) => {
    const member = intakeForms.find((f) => f.user_id === session.member_id);
    const amount = session.cpt_code ? (cptPricing[session.cpt_code] || 100) : 100;
    // Simulate denial reasons for demo
    const denialReason = denialReasons[index % denialReasons.length];

    return {
      ...session,
      memberName: member ? `${member.first_name} ${member.last_name}` : "Unknown",
      insuranceType: member?.insurance_type || "Unknown",
      amount,
      denialReason,
      deniedDate: session.updated_at,
    };
  });

  const resubmitMutation = useMutation({
    mutationFn: async (claimIds: string[]) => {
      const { error } = await supabase
        .from("telehealth_sessions")
        .update({ billing_status: "submitted" })
        .in("id", claimIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["denied-claims"] });
      setSelectedClaims([]);
      toast.success("Claims resubmitted successfully");
    },
    onError: () => {
      toast.error("Failed to resubmit claims");
    },
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedClaims(filteredClaims.map((c) => c.id));
    } else {
      setSelectedClaims([]);
    }
  };

  const handleSelectClaim = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedClaims([...selectedClaims, id]);
    } else {
      setSelectedClaims(selectedClaims.filter((c) => c !== id));
    }
  };

  const handleAppeal = (claim: any) => {
    setSelectedClaimForAppeal(claim);
    setAppealNotes("");
    setAppealDialogOpen(true);
  };

  const submitAppeal = () => {
    // In production, this would create an appeal record
    toast.success(`Appeal submitted for claim ${selectedClaimForAppeal.id.slice(0, 8)}`);
    setAppealDialogOpen(false);
    setSelectedClaimForAppeal(null);
  };

  // Calculate denial stats
  const denialsByReason = denialReasons.map((reason) => ({
    ...reason,
    count: deniedClaims.filter((c) => c.denialReason.code === reason.code).length,
  })).filter((r) => r.count > 0);

  const totalDeniedAmount = deniedClaims.reduce((sum, c) => sum + c.amount, 0);
  const avgDenialAmount = deniedClaims.length > 0 ? totalDeniedAmount / deniedClaims.length : 0;

  // Filter claims
  const filteredClaims = deniedClaims.filter((claim) => {
    const matchesSearch =
      claim.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.denialReason.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesReason = selectedReason === "all" || claim.denialReason.code === selectedReason;
    return matchesSearch && matchesReason;
  });

  const exportToCSV = () => {
    const headers = ["Claim ID", "Member", "Service Date", "Amount", "Denial Code", "Denial Reason", "Insurance"];
    const rows = filteredClaims.map((claim) => [
      claim.id,
      claim.memberName,
      format(parseISO(claim.scheduled_date), "MM/dd/yyyy"),
      claim.amount.toFixed(2),
      claim.denialReason.code,
      `"${claim.denialReason.description}"`,
      claim.insuranceType,
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `denials-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">Denials Management</h1>
              <p className="text-muted-foreground">Track, analyze, and appeal denied claims</p>
            </div>
            <div className="flex items-center gap-2">
              {selectedClaims.length > 0 && (
                <Button
                  onClick={() => resubmitMutation.mutate(selectedClaims)}
                  className="gap-2 bg-success hover:bg-success/90"
                >
                  <RefreshCw className="h-4 w-4" />
                  Resubmit ({selectedClaims.length})
                </Button>
              )}
              <Button onClick={exportToCSV} variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-destructive/10">
                    <XCircle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Denied</p>
                    <p className="text-2xl font-bold">{deniedClaims.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-destructive/10">
                    <DollarSign className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Denied Amount</p>
                    <p className="text-2xl font-bold">${totalDeniedAmount.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-warning/10">
                    <TrendingDown className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Denial Amount</p>
                    <p className="text-2xl font-bold">${avgDenialAmount.toFixed(2)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <AlertTriangle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Top Denial Reason</p>
                    <p className="text-lg font-bold">
                      {denialsByReason[0]?.code || "N/A"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Denial Reasons Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Denials by Reason</CardTitle>
              <CardDescription>Click a reason to filter the claims table</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {denialsByReason.map((reason) => (
                  <Badge
                    key={reason.code}
                    variant={selectedReason === reason.code ? "default" : "outline"}
                    className="cursor-pointer px-3 py-1.5"
                    onClick={() => setSelectedReason(reason.code === selectedReason ? "all" : reason.code)}
                  >
                    {reason.code} ({reason.count})
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Denials Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Denied Claims</CardTitle>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search claims..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={selectedReason} onValueChange={setSelectedReason}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Reasons</SelectItem>
                      {denialReasons.map((reason) => (
                        <SelectItem key={reason.code} value={reason.code}>
                          {reason.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : filteredClaims.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <XCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No denied claims found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedClaims.length === filteredClaims.length && filteredClaims.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Claim ID</TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Service Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Denial Code</TableHead>
                      <TableHead>Denial Reason</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClaims.map((claim) => (
                      <TableRow key={claim.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedClaims.includes(claim.id)}
                            onCheckedChange={(checked) => handleSelectClaim(claim.id, checked as boolean)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {claim.id.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="font-medium">{claim.memberName}</TableCell>
                        <TableCell>{format(parseISO(claim.scheduled_date), "MMM d, yyyy")}</TableCell>
                        <TableCell className="font-medium text-destructive">
                          ${claim.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="destructive">{claim.denialReason.code}</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                          {claim.denialReason.description}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => resubmitMutation.mutate([claim.id])}
                              title="Resubmit"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAppeal(claim)}
                              title="File Appeal"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Appeal Dialog */}
          <Dialog open={appealDialogOpen} onOpenChange={setAppealDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>File Appeal</DialogTitle>
                <DialogDescription>
                  Submit an appeal for claim {selectedClaimForAppeal?.id.slice(0, 8)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Member:</span>
                    <span className="font-medium">{selectedClaimForAppeal?.memberName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Denial Code:</span>
                    <Badge variant="destructive">{selectedClaimForAppeal?.denialReason.code}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-medium">${selectedClaimForAppeal?.amount.toFixed(2)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Appeal Justification</label>
                  <Textarea
                    placeholder="Explain why this claim should be reconsidered..."
                    value={appealNotes}
                    onChange={(e) => setAppealNotes(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAppealDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={submitAppeal} disabled={!appealNotes.trim()}>
                  Submit Appeal
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
}
